// =============================================================================
// SIREPPAR - Modal "Subir documento" usado al responder una observación
// =============================================================================
// Replica el modal de /documentos pero con partido/expediente pre-cargados.
// Soporta autocompletado de título y reutilización para tipos DNI.
// =============================================================================

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import FileUpload from './FileUpload';
import TipoDocumentoSelect from './TipoDocumentoSelect';
import { api, documentoService } from '../../services/api';
import {
  TIPOS_DNI_PERSONA,
  buildAutoTitleDni,
  TIPOS_SOLO_EXCEL,
  msgSoloExcel,
  ACCEPT_EXCEL_ODS,
  esArchivoExcelOds,
} from '../../utils/tiposDocumento';

interface DocumentoSubido {
  id: string;
  tipo: string;
  titulo: string;
  estadoRevision: string;
  nombreOriginal?: string;
}

interface SubirDocumentoRespuestaModalProps {
  isOpen: boolean;
  onClose: () => void;
  expedienteId: string;
  partidoId?: string;
  onUploaded: (doc: DocumentoSubido) => void;
  // Restringe los tipos ofrecidos (según intimación/observación del expediente).
  tiposPermitidos?: string[];
  // Etiquetas visuales por tipo (p.ej. sufijo "Promotor" en reconocimiento).
  labelOverrides?: Record<string, string>;
}

interface DniCheckResult {
  frente: { path: string; titulo: string; url: string } | null;
  dorso: { path: string; titulo: string; url: string } | null;
  persona: { nombre: string; apellido: string } | null;
}

export default function SubirDocumentoRespuestaModal({
  isOpen,
  onClose,
  expedienteId,
  partidoId,
  onUploaded,
  tiposPermitidos,
  labelOverrides,
}: SubirDocumentoRespuestaModalProps) {
  const [tipo, setTipo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [dniPersona, setDniPersona] = useState('');
  const [dniCheckLoading, setDniCheckLoading] = useState(false);
  const [dniCheckResult, setDniCheckResult] = useState<DniCheckResult | null>(null);
  const [dniReemplazar, setDniReemplazar] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  // Reset al cerrar/abrir
  useEffect(() => {
    if (!isOpen) {
      setTipo('');
      setTitulo('');
      setDescripcion('');
      setDniPersona('');
      setDniCheckResult(null);
      setDniReemplazar(false);
      setSubiendo(false);
    }
  }, [isOpen]);

  const esTipoDni = TIPOS_DNI_PERSONA.includes(tipo);
  // Algunos tipos solo admiten planilla de cálculo (Excel/ODS): ver TIPOS_SOLO_EXCEL.
  const soloExcel = TIPOS_SOLO_EXCEL.includes(tipo);

  const checkDniPersona = async (dni: string, tipoActual?: string) => {
    const clean = dni.replace(/\D/g, '');
    if (clean.length < 5) { setDniCheckResult(null); return; }
    setDniCheckLoading(true);
    try {
      const resp = await api.get(`/documentos/check-dni-persona/${clean}`);
      const data = resp.data.data;
      setDniCheckResult(data);
      if (data?.persona) {
        const tipoBase = tipoActual ?? tipo;
        setTitulo(buildAutoTitleDni(tipoBase, data.persona.nombre, data.persona.apellido, clean));
      }
    } catch {
      setDniCheckResult(null);
    } finally {
      setDniCheckLoading(false);
    }
  };

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    if (!tipo) {
      toast.error('Seleccioná un tipo de documento');
      return;
    }
    const cleanDni = dniPersona.replace(/\D/g, '');
    if (esTipoDni && !cleanDni) {
      toast.error('Ingresá el DNI de la persona antes de subir el archivo');
      return;
    }
    if (soloExcel && !esArchivoExcelOds(file.name)) {
      toast.error(msgSoloExcel(tipo));
      return;
    }
    setSubiendo(true);
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('tipo', tipo);
      formData.append('titulo', titulo || file.name);
      if (descripcion) formData.append('descripcion', descripcion);
      if (partidoId) formData.append('partidoId', partidoId);
      formData.append('expedienteId', expedienteId);
      if (cleanDni) formData.append('dniPersona', cleanDni);
      const res = await documentoService.upload(formData);
      const doc = res.data.data;
      onUploaded({
        id: doc.id,
        tipo: doc.tipo,
        titulo: doc.titulo,
        estadoRevision: doc.estadoRevisionAdmisibilidad ?? doc.estadoRevision ?? 'PENDIENTE',
        nombreOriginal: doc.nombreOriginal,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al subir documento');
    } finally {
      setSubiendo(false);
    }
  };

  const handleUsarExistente = async () => {
    const cleanDni = dniPersona.replace(/\D/g, '');
    const lado = tipo.includes('FRENTE') ? 'frente' : 'dorso';
    const existente = dniCheckResult?.[lado];
    if (!existente) return;
    setSubiendo(true);
    try {
      const res = await api.post('/documentos/vincular-existente', {
        tipo,
        titulo: titulo || existente.titulo,
        dniPersona: cleanDni,
        partidoId: partidoId || undefined,
        expedienteId,
        rutaArchivo: existente.path,
      });
      const doc = res.data.data;
      onUploaded({
        id: doc.id,
        tipo: doc.tipo,
        titulo: doc.titulo,
        estadoRevision: doc.estadoRevisionAdmisibilidad ?? doc.estadoRevision ?? 'PENDIENTE',
        nombreOriginal: doc.nombreOriginal,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al vincular documento');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Subir Documento" size="xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Documento *</label>
            <TipoDocumentoSelect
              value={tipo}
              tiposPermitidos={tiposPermitidos}
              labelOverrides={labelOverrides}
              onChange={(nuevoTipo) => {
                setTipo(nuevoTipo);
                setDniPersona('');
                setDniCheckResult(null);
                setDniReemplazar(false);
                setTitulo('');
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Título
              {esTipoDni && (
                <span className="ml-1 text-xs text-gray-400 font-normal">(se genera automáticamente)</span>
              )}
            </label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => !esTipoDni && setTitulo(e.target.value)}
              readOnly={esTipoDni}
              placeholder={esTipoDni ? 'Se genera al ingresar el DNI' : 'Se usará el nombre del archivo si está vacío'}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${esTipoDni ? 'bg-gray-50 text-gray-600 cursor-default' : ''}`}
            />
          </div>

          {esTipoDni && (
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">DNI de la persona *</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={dniPersona}
                  onChange={(e) => {
                    setDniPersona(e.target.value);
                    setTitulo('');
                    setDniCheckResult(null);
                    setDniReemplazar(false);
                  }}
                  onBlur={() => checkDniPersona(dniPersona, tipo)}
                  placeholder="Número de DNI"
                  className="border rounded-lg px-3 py-2 text-sm w-40"
                />
                {dniCheckLoading && <span className="text-xs text-gray-400">Buscando...</span>}
              </div>
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Descripción</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {(() => {
          if (esTipoDni) {
            const lado = tipo.includes('FRENTE') ? 'frente' : 'dorso';
            const existente = dniCheckResult?.[lado];
            if (existente && !dniReemplazar) {
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-800">DNI aprobado encontrado</p>
                      <p className="text-xs text-green-600 truncate">{existente.titulo}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.open(existente.url, '_blank')}
                      className="text-xs text-blue-600 hover:underline flex-shrink-0"
                    >
                      Ver
                    </button>
                    <button
                      type="button"
                      onClick={() => setDniReemplazar(true)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 flex-shrink-0"
                    >
                      Reemplazar
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleUsarExistente}
                    disabled={subiendo}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    {subiendo ? 'Vinculando...' : 'Usar este documento'}
                  </button>
                </div>
              );
            }
          }
          return (
            <FileUpload
              onUpload={handleUpload}
              maxSize={25}
              multiple={false}
              disabled={!tipo}
              accept={soloExcel ? ACCEPT_EXCEL_ODS : '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'}
              label="Seleccionar archivo"
              description={
                !tipo
                  ? 'Elegí primero el tipo de documento.'
                  : soloExcel
                  ? 'El Listado de Adherentes debe ser Excel (.xlsx/.xls) u ODS. Máximo 25MB.'
                  : 'PDF, Word, Excel o imágenes. Máximo 25MB.'
              }
            />
          );
        })()}

        {subiendo && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Subiendo documento...</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
